import React, { useEffect, useMemo, useState } from 'react';
import { withTranslation } from 'react-i18next';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import GetApp from '@mui/icons-material/GetApp';
import Search from '@mui/icons-material/Search';
import ILocalAGGrid, { getDefaultColDef } from '../../grid/iLocalAGGrid';
import {
  displaySafeText,
  fetchAdminEnablementHistory,
} from '../../../utils/productLicensing';
import '../feature-purchases/feature-purchases.css';

const ALL_VALUE = '__all__';
const ACTION_ENABLED = 'enabled';
const ACTION_DISABLED = 'disabled';

const formatWhen = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (_err) {
    return String(value);
  }
};

const csvEscape = (value) => `"${String(value == null ? '' : value).replace(/"/g, '""')}"`;

const toDayTime = (value) => {
  if (!value) return 0;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return 0;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
};

const ActionCell = (params) => {
  const enabled = params.data && params.data.action === ACTION_ENABLED;
  return (
    <span
      className={`feature-card-status ${enabled ? 'is-enabled' : 'is-locked'}`}
    >
      {params.value}
    </span>
  );
};

const FeatureEnablementHistoryReport = ({ t }) => {
  const isIlocalUser = localStorage.getItem('isIlocalUser') === 'true';
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [features, setFeatures] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [featureFilter, setFeatureFilter] = useState(ALL_VALUE);
  const [actionFilter, setActionFilter] = useState(ALL_VALUE);
  const [accountFilter, setAccountFilter] = useState(ALL_VALUE);

  const enabledLabel = t('Features.StatusEnabled', { defaultValue: 'Enabled' });
  const disabledLabel = t('LicensingAdmin.ActionDisabled', {
    defaultValue: 'Disabled',
  });

  const featureOptions = useMemo(
    () => [
      {
        id: ALL_VALUE,
        name: t('FeaturePurchases.AllFeatures', { defaultValue: 'All features' }),
      },
      ...(features || [])
        .map((feature) => ({
          id: feature.id,
          name: displaySafeText(feature.name, ''),
        }))
        .filter((feature) => feature.name),
    ],
    [features, t]
  );

  const accountOptions = useMemo(
    () => [
      {
        id: ALL_VALUE,
        name: t('EnablementHistory.AllAccounts', {
          defaultValue: 'All accounts',
        }),
      },
      ...(accounts || [])
        .map((account) => ({
          id: account.id,
          name: displaySafeText(account.name, ''),
        }))
        .filter((account) => account.name),
    ],
    [accounts, t]
  );

  const actionOptions = useMemo(
    () => [
      {
        id: ALL_VALUE,
        name: t('FeaturePurchases.AllStatuses', { defaultValue: 'All statuses' }),
      },
      { id: ACTION_ENABLED, name: enabledLabel },
      { id: ACTION_DISABLED, name: disabledLabel },
    ],
    [disabledLabel, enabledLabel, t]
  );

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const data = await fetchAdminEnablementHistory({ action: 'all' });
        if (!cancelled) {
          setRows((data && data.rows) || []);
          setFeatures((data && data.features) || []);
          setAccounts((data && data.accounts) || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            (err && err.message) ||
              t('EnablementHistory.LoadError', {
                defaultValue: 'Unable to load enablement history right now.',
              })
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    if (isIlocalUser) {
      load();
    } else {
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [isIlocalUser, t]);

  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return (rows || []).filter((row) => {
      if (actionFilter !== ALL_VALUE && row.action !== actionFilter) {
        return false;
      }
      if (featureFilter !== ALL_VALUE && row.feature_key !== featureFilter) {
        return false;
      }
      if (accountFilter !== ALL_VALUE && row.account_id !== accountFilter) {
        return false;
      }
      if (!query) {
        return true;
      }
      const haystack = [
        row.account_name,
        row.feature_title,
        row.actor_name,
        row.actor_email,
        row.action,
      ]
        .map((value) => String(value || '').toLowerCase())
        .join(' ');
      return haystack.indexOf(query) !== -1;
    });
  }, [rows, searchText, featureFilter, actionFilter, accountFilter]);

  const enabledCount = filteredRows.filter(
    (row) => row.action === ACTION_ENABLED
  ).length;
  const accountCount = new Set(
    filteredRows.map((row) => row.account_id || row.account_name)
  ).size;

  const selectedFeature =
    featureOptions.find((item) => item.id === featureFilter) || featureOptions[0];
  const selectedAction =
    actionOptions.find((item) => item.id === actionFilter) || actionOptions[0];
  const selectedAccount =
    accountOptions.find((item) => item.id === accountFilter) || accountOptions[0];

  const actionLabelFor = (action) => {
    if (action === ACTION_ENABLED) return enabledLabel;
    if (action === ACTION_DISABLED) return disabledLabel;
    return displaySafeText(action, '—');
  };

  const columnDefs = useMemo(
    () => [
      {
        field: 'created_on',
        colId: 'created_on',
        headerName: t('LicensingAdmin.When', { defaultValue: 'When' }),
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: (filterLocalDateAtMidnight, cellValue) => {
            const cellTime = toDayTime(cellValue);
            const filterTime = toDayTime(filterLocalDateAtMidnight);
            if (cellTime < filterTime) return -1;
            if (cellTime > filterTime) return 1;
            return 0;
          },
          buttons: ['clear'],
        },
        valueFormatter: (params) => formatWhen(params.value),
        minWidth: 200,
      },
      {
        field: 'action',
        colId: 'action',
        headerName: t('LicensingAdmin.Action', { defaultValue: 'Action' }),
        filter: 'agSetColumnFilter',
        valueGetter: (params) => actionLabelFor(params.data && params.data.action),
        cellRenderer: ActionCell,
        minWidth: 140,
      },
      {
        field: 'feature_title',
        colId: 'feature_title',
        headerName: t('FeaturePurchases.Feature', { defaultValue: 'Feature' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.feature_title, '—'),
        minWidth: 180,
      },
      {
        field: 'account_name',
        colId: 'account_name',
        headerName: t('FeaturePurchases.Account', { defaultValue: 'Account' }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.account_name, '—'),
        minWidth: 180,
      },
      {
        field: 'actor_name',
        colId: 'actor_name',
        headerName: t('EnablementHistory.ChangedBy', {
          defaultValue: 'Changed by',
        }),
        valueGetter: (params) =>
          displaySafeText(
            params.data && params.data.actor_name,
            displaySafeText(params.data && params.data.actor_email, '—')
          ),
        minWidth: 180,
      },
      {
        field: 'actor_email',
        colId: 'actor_email',
        headerName: t('EnablementHistory.ActorEmail', {
          defaultValue: 'Email',
        }),
        valueGetter: (params) =>
          displaySafeText(params.data && params.data.actor_email, '—'),
        minWidth: 200,
      },
    ],
    [disabledLabel, enabledLabel, t]
  );

  const handleExport = () => {
    const header = [
      t('LicensingAdmin.When', { defaultValue: 'When' }),
      t('LicensingAdmin.Action', { defaultValue: 'Action' }),
      t('FeaturePurchases.Feature', { defaultValue: 'Feature' }),
      t('FeaturePurchases.Account', { defaultValue: 'Account' }),
      t('EnablementHistory.ChangedBy', { defaultValue: 'Changed by' }),
      t('EnablementHistory.ActorEmail', { defaultValue: 'Email' }),
    ];
    const lines = [
      header.map(csvEscape).join(','),
      ...filteredRows.map((row) =>
        [
          formatWhen(row.created_on),
          actionLabelFor(row.action),
          displaySafeText(row.feature_title, '—'),
          displaySafeText(row.account_name, '—'),
          displaySafeText(row.actor_name, '—'),
          displaySafeText(row.actor_email, '—'),
        ]
          .map(csvEscape)
          .join(',')
      ),
    ];
    const blob = new Blob([lines.join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'feature-enablement-history.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isIlocalUser) {
    return (
      <div className="feature-purchases">
        <p className="licensing-empty">
          {t('LicensingAdmin.Forbidden', {
            defaultValue: 'This page is only available to iLocal members.',
          })}
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="feature-purchases">
        <CircularProgress size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="feature-purchases">
        <p className="licensing-empty">{error}</p>
      </div>
    );
  }

  return (
    <div className="feature-purchases">
      <div className="feature-purchases-hero">
        <p className="features-hero-eyebrow">
          {t('EnablementHistory.Eyebrow', { defaultValue: 'Licensing audit' })}
        </p>
        <p className="features-intro">
          {t('EnablementHistory.Intro', {
            defaultValue:
              'See who enabled or disabled each licensed feature, for which pharmacy, and when.',
          })}
        </p>
        <div className="features-hero-meta">
          <span>
            {t('EnablementHistory.EnabledCount', {
              defaultValue: '{{count}} enabled',
              count: enabledCount,
            })}
          </span>
          <span>
            {t('FeaturePurchases.AccountCount', {
              defaultValue: '{{count}} accounts',
              count: accountCount,
            })}
          </span>
        </div>
      </div>

      <div className="feature-purchases-toolbar">
        <TextField
          className="feature-purchases-search"
          variant="outlined"
          size="small"
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          placeholder={t('EnablementHistory.SearchPlaceholder', {
            defaultValue: 'Search account, feature, person, or email...',
          })}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: '#64748b' }} />
              </InputAdornment>
            ),
          }}
        />
        <Autocomplete
          className="feature-purchases-filter"
          options={accountOptions}
          value={selectedAccount}
          disableClearable
          getOptionLabel={(option) => (option && option.name) || ''}
          isOptionEqualToValue={(option, value) =>
            !!(option && value && option.id === value.id)
          }
          onChange={(_event, value) =>
            setAccountFilter((value && value.id) || ALL_VALUE)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('FeaturePurchases.Account', { defaultValue: 'Account' })}
            />
          )}
        />
        <Autocomplete
          className="feature-purchases-filter"
          options={featureOptions}
          value={selectedFeature}
          disableClearable
          getOptionLabel={(option) => (option && option.name) || ''}
          isOptionEqualToValue={(option, value) =>
            !!(option && value && option.id === value.id)
          }
          onChange={(_event, value) =>
            setFeatureFilter((value && value.id) || ALL_VALUE)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('FeaturePurchases.Feature', { defaultValue: 'Feature' })}
            />
          )}
        />
        <Autocomplete
          className="feature-purchases-filter"
          options={actionOptions}
          value={selectedAction}
          disableClearable
          getOptionLabel={(option) => (option && option.name) || ''}
          isOptionEqualToValue={(option, value) =>
            !!(option && value && option.id === value.id)
          }
          onChange={(_event, value) =>
            setActionFilter((value && value.id) || ALL_VALUE)
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label={t('LicensingAdmin.Action', { defaultValue: 'Action' })}
            />
          )}
        />
        <Button
          className="feature-purchases-export"
          variant="contained"
          startIcon={<GetApp />}
          disabled={filteredRows.length === 0}
          onClick={handleExport}
          sx={{
            textTransform: 'none',
            backgroundColor: '#00a7e1',
            borderRadius: '10px',
            fontWeight: 600,
            boxShadow: '0 8px 18px rgba(0, 167, 225, 0.28)',
            '&:hover': { backgroundColor: '#0096cc' },
          }}
        >
          {t('FeaturePurchases.Export', { defaultValue: 'Export CSV' })}
        </Button>
      </div>

      <ILocalAGGrid
        className="ag-theme-alpine feature-purchases-grid"
        columnDefs={columnDefs}
        defaultColDef={getDefaultColDef()}
        rowModelType="clientSide"
        rowData={filteredRows}
        pagination={true}
        paginationPageSize={25}
        paginationPageSizeSelector={[10, 20, 25]}
        rowHeight={48}
        overlayNoRowsMessage={t('EnablementHistory.Empty', {
          defaultValue: 'No enablement history matches this search.',
        })}
        getRowId={(params) => {
          const row = params.data || {};
          if (row.id) {
            return String(row.id);
          }
          return [
            row.account_id,
            row.feature_key,
            row.action,
            row.created_on,
            row.actor_email,
          ].join('|');
        }}
      />
    </div>
  );
};

export default withTranslation()(FeatureEnablementHistoryReport);



// WEBPACK FOOTER //
// ./src/components/reports/feature-enablement-history/index.js